<script lang="ts">
import type { InputNumberProps, InputNumberSlots } from '#ui/types';
import { Decimal as DecimalBase } from 'decimal.js-light';

/**
 * decimal.js-light rounds every arithmetic result to a global 20 significant digits, which
 * silently truncates columns wider than that (Postgres `numeric` allows up to 1000). Step on an
 * isolated constructor that never rounds rather than reconfiguring the shared one.
 */
const Decimal = DecimalBase.clone({ precision: 1e9 });
type Decimal = DecimalBase;

export interface InputDecimalNumberProps
	extends Pick<
		InputNumberProps,
		| 'placeholder'
		| 'color'
		| 'variant'
		| 'size'
		| 'highlight'
		| 'orientation'
		| 'increment'
		| 'incrementIcon'
		| 'incrementDisabled'
		| 'decrement'
		| 'decrementIcon'
		| 'decrementDisabled'
		| 'disabled'
		| 'readonly'
		| 'required'
		| 'id'
		| 'name'
		| 'class'
		| 'ui'
		| 'autofocus'
		| 'autofocusDelay'
	> {
	/**
	 * Input may receive stale field values with another runtime type. Invalid
	 * values are discarded before reaching decimal parsing.
	 */
	modelValue?: unknown;
	/** Default value when modelValue is undefined */
	defaultValue?: string;
	/** Minimum value (as string for arbitrary precision) */
	min?: string;
	/** Maximum value (as string for arbitrary precision) */
	max?: string;
	/** Step value (as string for arbitrary precision) */
	step?: string;
	/** Total number of significant digits allowed (not counting minus sign) */
	precision?: number;
	/** Number of decimal places allowed */
	scale?: number;
}

export interface InputDecimalNumberEmits {
	'update:modelValue': [value: string | null];
	'blur': [event: FocusEvent];
	'change': [event: Event];
}

export type InputDecimalNumberSlots = InputNumberSlots;
</script>

<script setup lang="ts">
import { createReusableTemplate, reactiveComputed } from '@vueuse/core';
import { tv } from 'tailwind-variants';
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useAppConfig } from '#app/config';
import inputNumber from '#build/ui/input-number';
import { useFormField } from '#layers/ui/composables/use-form-field';
import { useMergedTheme } from '#layers/ui/composables/use-merged-theme';
import { useFieldGroup } from '#ui/composables/useFieldGroup';

const {
	step: stepProp = '1',
	orientation = 'vertical',
	increment = true,
	decrement = true,
	incrementIcon: incrementIconProp,
	incrementDisabled,
	decrementIcon: decrementIconProp,
	decrementDisabled,
	modelValue,
	defaultValue,
	min,
	max,
	precision,
	scale,
	readonly,
	color,
	variant,
	highlight,
	autofocus,
	autofocusDelay,
	id: idProp,
	name: nameProp,
	disabled: disabledProp,
	size: sizeProp,
	class: className,
	ui: uiProp,
} = defineProps<InputDecimalNumberProps>();

const emit = defineEmits<InputDecimalNumberEmits>();

defineSlots<InputDecimalNumberSlots>();

const appConfig = useAppConfig();
const theme = useMergedTheme({ inputNumber });

const {
	id,
	name,
	disabled,
	size: formGroupSize,
	emitFormBlur,
	emitFormChange,
	emitFormInput,
} = useFormField<InputDecimalNumberProps>(() => ({ id: idProp, name: nameProp, size: sizeProp, color, highlight, disabled: disabledProp }));
const { size: fieldGroupSize, orientation: fieldGroupOrientation } = useFieldGroup<InputDecimalNumberProps>(reactiveComputed(() => ({ size: sizeProp })));

const localValue = ref<string>('');
const isTouched = ref(false);
const isFocused = ref(false);
const inputRef = useTemplateRef<HTMLInputElement>('inputRef');

defineExpose({
	inputRef: computed(() => inputRef.value),
});

const minDecimal = computed(() => {
	if (min === undefined) return null;

	try {
		return new Decimal(min);
	}
	catch {
		return null;
	}
});

const maxDecimal = computed(() => {
	if (max === undefined) return null;

	try {
		return new Decimal(max);
	}
	catch {
		return null;
	}
});

const stepDecimal = computed(() => {
	try {
		return new Decimal(stepProp ?? '1');
	}
	catch {
		return new Decimal(1);
	}
});

const inputSize = computed(() => fieldGroupSize.value || formGroupSize.value);

const incrementIcon = computed(
	() => incrementIconProp || (orientation === 'horizontal' ? appConfig.ui.icons.plus : appConfig.ui.icons.chevronUp),
);
const decrementIcon = computed(
	() => decrementIconProp || (orientation === 'horizontal' ? appConfig.ui.icons.minus : appConfig.ui.icons.chevronDown),
);

// A read-only field drops the steppers rather than showing a control that refuses to act; that
// also releases the padding the theme reserves for them.
const showIncrement = computed(() => !!increment && !readonly);
const showDecrement = computed(() => !!decrement && !readonly);

const ui = computed(() =>
	tv({ extend: tv(theme) })({
		color,
		variant,
		size: inputSize.value,
		highlight,
		orientation,
		fieldGroup: fieldGroupOrientation.value,
		increment: orientation === 'vertical' ? showIncrement.value || showDecrement.value : showIncrement.value,
		decrement: orientation === 'vertical' ? false : showDecrement.value,
		disabled: disabled.value,
	}),
);

function initLocalValue() {
	if (modelValue === null || modelValue === undefined) {
		localValue.value = defaultValue ?? '';
	}
	else if (typeof modelValue !== 'string') {
		// Non-string values (e.g. boolean from a stale field type) are discarded — they're invalid for a decimal input
		localValue.value = '';
	}
	else {
		localValue.value = modelValue;
	}
}

watch(
	() => modelValue,
	() => {
		if (!isFocused.value) {
			initLocalValue();
		}
	},
	{ immediate: true },
);

function normalizeDecimalSeparator(value: string): string {
	return value.replaceAll(',', '.');
}

function isPartiallyValid(value: string): boolean {
	if (value === '' || value === '-') return true;

	const hasLeadingMinus = value.startsWith('-');
	const numericPart = hasLeadingMinus ? value.slice(1) : value;

	const decimalCount = (numericPart.match(/\./g) || []).length;
	if (decimalCount > 1) return false;

	if (!/^\d*(?:\.\d*)?$/.test(numericPart)) return false;

	// (minus doesn't count toward precision)
	if (precision !== undefined) {
		const digits = numericPart.replace('.', '');
		if (digits.length > precision) return false;
	}

	if (scale !== undefined) {
		const decimalIndex = numericPart.indexOf('.');

		if (decimalIndex !== -1) {
			const decimalPlaces = numericPart.length - decimalIndex - 1;
			if (decimalPlaces > scale) return false;
		}
	}

	return true;
}

function isFullyValid(value: string): boolean {
	if (value === '') return true;
	if (value === '-') return false;
	if (!isPartiallyValid(value)) return false;

	try {
		const decimal = new Decimal(value);
		if (minDecimal.value && decimal.lt(minDecimal.value)) return false;
		if (maxDecimal.value && decimal.gt(maxDecimal.value)) return false;
		return true;
	}
	catch {
		return false;
	}
}

function clamp(value: string): string {
	if (value === '' || value === '-') return value;

	try {
		let decimal = new Decimal(value);
		if (minDecimal.value && decimal.lt(minDecimal.value)) decimal = minDecimal.value;
		if (maxDecimal.value && decimal.gt(maxDecimal.value)) decimal = maxDecimal.value;
		return decimal.toString();
	}
	catch {
		return value;
	}
}

function stepValue(direction: 1 | -1, multiplier: number = 1) {
	if (disabled.value || readonly) return;

	let currentValue: Decimal;

	if (localValue.value === '' || localValue.value === '-') {
		currentValue = new Decimal(0);
	}
	else {
		try {
			currentValue = new Decimal(localValue.value);
		}
		catch {
			return;
		}
	}

	const step = stepDecimal.value.times(multiplier).times(direction);
	let newValue = currentValue.plus(step);

	if (minDecimal.value && newValue.lt(minDecimal.value)) newValue = minDecimal.value;
	if (maxDecimal.value && newValue.gt(maxDecimal.value)) newValue = maxDecimal.value;

	// Scale caps the decimal places, it doesn't pad them: stepping 12.5 must not yield 12.50.
	const result = (scale !== undefined ? newValue.toDecimalPlaces(scale) : newValue).toString();
	localValue.value = result;
	applyValue(result);
}

function applyValue(value: string) {
	const normalizedValue = value === '' ? null : value;
	emit('update:modelValue', normalizedValue);
	emitFormChange();
	emitFormInput();
}

function onBeforeInput(event: InputEvent) {
	if (readonly || disabled.value) {
		event.preventDefault();
		return;
	}

	const input = event.target as HTMLInputElement;
	const data = event.data;

	if (!data) return;

	const normalizedData = normalizeDecimalSeparator(data);

	const selectionStart = input.selectionStart ?? 0;
	const selectionEnd = input.selectionEnd ?? 0;
	const currentValue = input.value;
	const prospectiveValue = currentValue.slice(0, selectionStart) + normalizedData + currentValue.slice(selectionEnd);

	if (!isPartiallyValid(prospectiveValue)) {
		event.preventDefault();
		return;
	}

	if (/e/i.test(normalizedData)) {
		event.preventDefault();
		return;
	}

	if (normalizedData.includes('-') && (selectionStart !== 0 || currentValue.includes('-'))) {
		event.preventDefault();
	}
}

// Handle input event (fallback for paste/IME)
function onInput(event: Event) {
	const input = event.target as HTMLInputElement;
	const value = normalizeDecimalSeparator(input.value);

	if (!isPartiallyValid(value)) {
		input.value = localValue.value;
		return;
	}

	localValue.value = value;
	isTouched.value = true;
}

function onFocus() {
	isFocused.value = true;
}

function onBlur(event: FocusEvent) {
	isFocused.value = false;
	isTouched.value = true;

	let value = localValue.value;

	if (value === '-' || value === '-.') {
		value = '';
		localValue.value = value;
	}

	if (value.endsWith('.')) {
		value = value.slice(0, -1);
		localValue.value = value;
	}

	const clamped = clamp(value);

	if (clamped !== value) {
		localValue.value = clamped;
		value = clamped;
	}

	applyValue(value);
	emit('blur', event);
	emitFormBlur();
}

function onChange(event: Event) {
	emit('change', event);
	emitFormChange();
}

function onKeyDown(event: KeyboardEvent) {
	if (disabled.value || readonly) return;

	switch (event.key) {
		case 'ArrowUp':
			event.preventDefault();
			stepValue(1);
			break;
		case 'ArrowDown':
			event.preventDefault();
			stepValue(-1);
			break;
		case 'PageUp':
			event.preventDefault();
			stepValue(1, 10);
			break;
		case 'PageDown':
			event.preventDefault();
			stepValue(-1, 10);
			break;
		case 'Home':
			if (min !== undefined) {
				event.preventDefault();
				localValue.value = min;
				applyValue(min);
			}

			break;
		case 'End':
			if (max !== undefined) {
				event.preventDefault();
				localValue.value = max;
				applyValue(max);
			}

			break;
		case 'Enter':
			event.preventDefault();

			if (isFullyValid(localValue.value)) {
				const clamped = clamp(localValue.value);
				localValue.value = clamped;
				applyValue(clamped);
			}

			break;
	}
}

// Exact plain notation: a JS number would round values beyond double precision, and `toString()`
// switches to exponent notation for large magnitudes. Browsers still expose `aria-valuenow` to
// assistive tech as a double, so the same text also goes out as `aria-valuetext`, which is announced verbatim.
function toAriaValue(value: string): string | undefined {
	if (value === '' || value === '-') return undefined;

	try {
		return new Decimal(value).toFixed();
	}
	catch {
		return undefined;
	}
}

const ariaValueNow = computed(() => toAriaValue(localValue.value));
const ariaValueMin = computed(() => min === undefined ? undefined : toAriaValue(min));
const ariaValueMax = computed(() => max === undefined ? undefined : toAriaValue(max));

const ariaInvalid = computed<'true' | undefined>(() => {
	if (!isTouched.value) return;
	return !isFullyValid(localValue.value) ? 'true' : undefined;
});

const isIncrementDisabled = computed(() => {
	if (disabled.value || readonly || incrementDisabled) return true;
	if (!maxDecimal.value) return false;
	if (localValue.value === '' || localValue.value === '-') return false;

	try {
		return new Decimal(localValue.value).gte(maxDecimal.value);
	}
	catch {
		return false;
	}
});

const isDecrementDisabled = computed(() => {
	if (disabled.value || readonly || decrementDisabled) return true;
	if (!minDecimal.value) return false;
	if (localValue.value === '' || localValue.value === '-') return false;

	try {
		return new Decimal(localValue.value).lte(minDecimal.value);
	}
	catch {
		return false;
	}
});

function autoFocus() {
	if (autofocus) {
		inputRef.value?.focus();
	}
}

onMounted(() => {
	setTimeout(() => {
		autoFocus();
	}, autofocusDelay);
});

const [DefineStepButton, ReuseStepButton] = createReusableTemplate<{
	'direction': 1 | -1;
	'icon': string;
	'disabled': boolean;
	'aria-label': string;
}>();
</script>

<template>
	<DefineStepButton v-slot="{ direction, icon, disabled: isDisabled, ...attrs }">
		<DButton
			:icon
			:color="color ?? 'neutral'"
			:size="inputSize"
			:disabled="isDisabled"
			:aria-label="attrs['aria-label']"
			tabindex="-1"
			variant="link"
			class="text-dimmed"
			@click="stepValue(direction)"
		/>
	</DefineStepButton>

	<div
		role="group"
		:class="ui.root({ class: [uiProp?.root, className] })"
		v-bind="$attrs"
	>
		<input
			:id
			ref="inputRef"
			type="text"
			inputmode="decimal"
			role="spinbutton"
			aria-roledescription="Number field"
			:name
			:placeholder
			:disabled
			:readonly
			:required
			:aria-valuenow="ariaValueNow"
			:aria-valuetext="ariaValueNow"
			:aria-valuemin="ariaValueMin"
			:aria-valuemax="ariaValueMax"
			:aria-invalid
			:value="localValue"
			:class="ui.base({ class: uiProp?.base })"
			@beforeinput="onBeforeInput"
			@input="onInput"
			@focus="onFocus"
			@blur="onBlur"
			@change="onChange"
			@keydown="onKeyDown"
		>

		<div
			v-if="showDecrement && orientation === 'horizontal'"
			:class="ui.decrement({ class: uiProp?.decrement })"
		>
			<slot name="decrement">
				<ReuseStepButton :direction="-1" :icon="decrementIcon" :disabled="isDecrementDisabled" :aria-label="$t('common.decrement')" />
			</slot>
		</div>

		<div
			v-if="showIncrement"
			:class="ui.increment({ class: uiProp?.increment })"
		>
			<slot name="increment">
				<ReuseStepButton :direction="1" :icon="incrementIcon" :disabled="isIncrementDisabled" :aria-label="$t('common.increment')" />
			</slot>
		</div>

		<div
			v-if="showDecrement && orientation === 'vertical'"
			:class="ui.decrement({ class: uiProp?.decrement })"
		>
			<slot name="decrement">
				<ReuseStepButton :direction="-1" :icon="decrementIcon" :disabled="isDecrementDisabled" :aria-label="$t('common.decrement')" />
			</slot>
		</div>
	</div>
</template>
